import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  getKeywordNameAPI,
  getKeywordRankHistoryAPI,
  getAdKeywordRankHistoryAPI,
} from "../../../../../api/index";
import { useAuth } from "../../../../../hooks/useAuth"; // ✅ useAuth 추가
import "./RankCheckModalPopup.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const RankCheckModal = ({ isOpen, onClose, adData }) => {
  const { user } = useAuth(); // ✅ useAuth에서 user 가져오기
  const [selectedAdName, setSelectedAdName] = useState("");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overlayMessage, setOverlayMessage] = useState(null);
  const [yAxisRange, setYAxisRange] = useState({ min: 1, max: 100 }); // Y축 범위 state 추가
  const [sortedRankHistory, setSortedRankHistory] = useState([]); // ✅ 추가
  const isRankDataLoading = useRef(false);

  // 광고 데이터가 변경될 때마다 순위 데이터 로딩
  useEffect(() => {
    const loadRankData = async () => {
      if (!isOpen || !adData) {
        return;
      }

      // 이미 로딩 중이면 실행하지 않음
      if (isRankDataLoading.current) {
        return;
      }

      isRankDataLoading.current = true;
      setLoading(true);
      setError(null);
      setOverlayMessage(null);

      try {
        // ✅ 올바른 로그인 상태 감지
        if (!user?.id) {
          // 로그아웃 상태일 때 목데이터 사용
          const mockRankHistory = generateMockRankHistory(adData);

          if (mockRankHistory.length === 0) {
            setOverlayMessage("목데이터 순위체크를 생성할 수 없습니다.");
            return;
          }

          // 목데이터 순위체크 설정
          setSelectedAdName(adData.mainKeyword || "목데이터 키워드");
          setSortedRankHistory(mockRankHistory);

          // 날짜별 순위 데이터 준비
          const labels = mockRankHistory.map((item) => {
            const utcDate = new Date(item.date);
            const kstDate = new Date(utcDate.getTime() + 9 * 60 * 60 * 1000);
            return kstDate.toLocaleDateString("ko-KR", {
              month: "numeric",
              day: "numeric",
            });
          });

          // 999를 500으로 변환하여 차트 포인트 위치 조정
          const ranks = mockRankHistory.map((item) => {
            if (item.rank === 999) {
              return 500;
            }
            return item.rank;
          });

          // Y축 동적 계산
          const yAxisRange = calculateYAxisRange(ranks);
          setYAxisRange(yAxisRange);

          // options 생성
          const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    const originalRank =
                      mockRankHistory[context.dataIndex]?.rank;
                    if (originalRank === 999) {
                      return `순위: 500+`;
                    }
                    return `순위: ${context.parsed.y}`;
                  },
                },
              },
            },
            scales: {
              y: {
                reverse: true,
                min: yAxisRange.min,
                max: yAxisRange.max,
                ticks: {
                  stepSize: yAxisRange.stepSize,
                  callback: function (value, index, values) {
                    if (value === 500 && ranks.includes(500)) {
                      return "500+";
                    }
                    return value;
                  },
                },
              },
              x: {
                grid: {
                  display: false,
                },
              },
            },
          };

          setChartData({
            labels,
            datasets: [
              {
                label: "순위",
                data: ranks,
                fill: false,
                borderColor: "#007bff",
                tension: 0.1,
                pointBackgroundColor: "#007bff",
                pointRadius: 5,
              },
            ],
          });

          setOptions(options);
          return;
        }

        // 로그인 상태일 때 기존 API 로직 실행
        // 1. 키워드 이름 조회
        const keywordResponse = await getKeywordNameAPI(adData.ad_id);
        const keywords = keywordResponse.data?.data || [];

        // MAIN 타입 키워드 찾기
        const mainKeyword = keywords.find((keyword) => keyword.type === "MAIN");

        if (!mainKeyword) {
          setOverlayMessage("메인 키워드를 찾을 수 없습니다.");
          return;
        }

        setSelectedAdName(mainKeyword.name);

        // detail이 0이면 순위 확인을 하지 않는 광고
        if (adData.detail === 0) {
          setOverlayMessage("순위 확인을 하지 않는 광고입니다.");
          return;
        }

        // 2. 광고 ID로 키워드 순위 히스토리 조회
        const adRankResponse = await getAdKeywordRankHistoryAPI(adData.ad_id);
        const adRankHistory = adRankResponse.data?.data || [];

        // 날짜별 순위 데이터 정리 (최신 10개를 오래된 순서로 정렬)
        const sortedRankHistory = adRankHistory
          .sort((a, b) => new Date(b.date) - new Date(a.date)) // 최신순 정렬
          .slice(0, 10)
          .reverse(); // 오래된 순서로 뒤집기

        // ✅ sortedRankHistory를 state로 저장
        setSortedRankHistory(sortedRankHistory);

        if (sortedRankHistory.length === 0) {
          setOverlayMessage("순위체크가 안 되었습니다.");
          return;
        }

        // 날짜별 순위 데이터만 사용하여 차트 생성 (시작 라벨 제거)

        // 날짜별 순위 데이터 준비 (오래된 순서)
        // UTC 시간을 한국 시간으로 변환하여 날짜 표시
        const labels = sortedRankHistory.map((item) => {
          const utcDate = new Date(item.date);
          const kstDate = new Date(utcDate.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
          return kstDate.toLocaleDateString("ko-KR", {
            month: "numeric",
            day: "numeric",
          });
        });

        const ranks = sortedRankHistory.map((item) => {
          // 999를 500으로 변환하여 차트 포인트 위치 조정
          if (item.rank === 999) {
            return 500;
          }
          return item.rank;
        });

        // Y축 동적 계산 (시각적 효과 극대화)
        const yAxisRange = calculateYAxisRange(ranks);

        // Y축 범위를 state로 저장
        setYAxisRange(yAxisRange);

        // ✅ options를 useEffect 내부에서 생성
        const options = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  // 999 순위일 때 "500+"로 표시
                  const originalRank =
                    sortedRankHistory[context.dataIndex]?.rank;
                  if (originalRank === 999) {
                    return `순위: 500+`;
                  }
                  return `순위: ${context.parsed.y}`;
                },
              },
            },
          },
          scales: {
            y: {
              reverse: true,
              min: yAxisRange.min,
              max: yAxisRange.max,
              ticks: {
                stepSize: yAxisRange.stepSize, // 균등한 간격 적용
                callback: function (value, index, values) {
                  // 500을 500+로 표시 (999가 변환된 경우)
                  if (
                    value === 500 &&
                    chartData?.datasets?.[0]?.data?.includes(500)
                  ) {
                    return "500+";
                  }
                  return value;
                },
              },
            },
            x: {
              grid: {
                display: false,
              },
            },
          },
        };

        setChartData({
          labels,
          datasets: [
            {
              label: "순위",
              data: ranks,
              fill: false,
              borderColor: "#007bff",
              tension: 0.1,
              pointBackgroundColor: "#007bff",
              pointRadius: 5,
            },
          ],
        });

        // ✅ options도 state로 저장
        setOptions(options);
      } catch (error) {
        console.error("순위 데이터 로딩 오류:", error);
        setError("순위 데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
        isRankDataLoading.current = false;
      }
    };

    loadRankData();
  }, [isOpen, adData, user?.id]); // ✅ user?.id 의존성 추가

  // ✅ options state 추가
  const [options, setOptions] = useState({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        reverse: true,
        min: 1,
        max: 100,
        ticks: {
          stepSize: 20,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  });

  // ✅ 목데이터 순위체크 생성 함수 (항상 상승)
  const generateMockRankHistory = (ad) => {
    const mockHistory = [];
    const startRank = ad.initialRank || 50;
    const currentRank = ad.currentRank || 10;

    // 최근 10일간의 순위 변화 생성 (항상 상승)
    for (let i = 9; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);

      // 순위 변화 시뮬레이션 (시작순위에서 현재순위로 점진적 상승)
      let rank;
      if (i === 9) {
        rank = startRank; // 10일 전: 시작순위 (가장 낮은 순위)
      } else if (i === 0) {
        rank = currentRank; // 오늘: 현재순위 (가장 높은 순위)
      } else {
        // 중간 날짜들: 시작순위와 현재순위 사이의 점진적 상승
        const progress = (9 - i) / 9; // 진행률 (0~1)
        const baseRank = startRank + (currentRank - startRank) * progress;

        // 약간의 변동성 추가 (하지만 항상 상승하는 방향)
        const variation = Math.random() * 0.1; // 10% 변동성
        rank = Math.max(
          currentRank,
          Math.round(baseRank + variation * (startRank - currentRank))
        );
      }

      // 999 순위 시뮬레이션 (일정 확률로, 하지만 상승하는 방향에서만)
      if (Math.random() < 0.05 && i > 5 && rank > 100) {
        // 5% 확률, 5일 전부터, 높은 순위에서만
        rank = 999;
      }

      mockHistory.push({
        _id: `mock_${ad.ad_id}_${i}`,
        akr_id: `mock_${i}`,
        keyword_id: `mock_keyword_${ad.ad_id}`,
        ad_id: ad.ad_id,
        rank: rank,
        date: date.toISOString(),
        createdAt: date.toISOString(),
        updatedAt: date.toISOString(),
      });
    }

    return mockHistory;
  };

  // Y축 동적 계산 함수
  const calculateYAxisRange = (rankData) => {
    // 데이터가 하나인 경우 특별 처리
    if (rankData.length === 1) {
      const singleRank = rankData[0];
      if (singleRank === 500) {
        // 999가 500으로 변환된 경우
        return { min: 450, max: 500, stepSize: 10 }; // 10단위 간격
      } else {
        // 단일 순위 주변에 여백 추가
        const margin = Math.max(10, Math.ceil(singleRank * 0.2));
        const min = Math.max(1, singleRank - margin);
        const max = singleRank + margin;
        const stepSize = Math.max(1, Math.ceil((max - min) / 5)); // 최소 1 보장
        return { min, max, stepSize };
      }
    }

    const maxRank = Math.max(...rankData);
    const minRank = Math.min(...rankData);

    // 데이터 범위 계산
    const dataRange = maxRank - minRank;

    // 동일한 순위 값인 경우 특별 처리 (예: 7, 7)
    if (dataRange === 0) {
      const margin = Math.max(10, Math.ceil(maxRank * 0.2));
      const min = Math.max(1, maxRank - margin);
      const max = maxRank + margin;
      const stepSize = Math.max(1, Math.ceil((max - min) / 5)); // 최소 1 보장
      return { min, max, stepSize };
    }

    // Y축 설정 전략
    let yMin, yMax, stepSize;

    if (dataRange <= 50) {
      // 작은 변화: minRank 주변으로 집중 (여백 30%)
      yMin = Math.max(1, minRank - Math.ceil(dataRange * 0.3));
      yMax = maxRank + Math.ceil(dataRange * 0.3);
      stepSize = Math.max(1, Math.ceil((yMax - yMin) / 5)); // 최소 1 보장
    } else if (dataRange <= 100) {
      // 중간 변화: 적당한 여백 (20%)
      yMin = Math.max(1, minRank - Math.ceil(dataRange * 0.2));
      yMax = maxRank + Math.ceil(dataRange * 0.2);
      stepSize = Math.max(1, Math.ceil((yMax - yMin) / 6)); // 최소 1 보장
    } else if (dataRange <= 200) {
      // 큰 변화: 여백 줄이기 (15%)
      yMin = Math.max(1, minRank - Math.ceil(dataRange * 0.15));
      yMax = maxRank + Math.ceil(dataRange * 0.15);
      stepSize = Math.max(1, Math.ceil((yMax - yMin) / 7)); // 최소 1 보장
    } else {
      // 매우 큰 변화: 최소 여백 (10%)
      yMin = Math.max(1, minRank - Math.ceil(dataRange * 0.1));
      yMax = maxRank + Math.ceil(dataRange * 0.1);
      stepSize = Math.max(1, Math.ceil((yMax - yMin) / 8)); // 최소 1 보장
    }

    // 500은 특별 처리 (500+로 표시)
    if (maxRank === 500) {
      yMax = 500;
      stepSize = 10; // 10단위 간격
    }

    return { min: yMin, max: yMax, stepSize };
  };

  if (!isOpen) return null;

  return (
    <div className="rank-check-modal-popup-overlay" onClick={onClose}>
      <div
        className="rank-check-modal-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="rank-check-modal-popup-header">
          <h2>순위체크</h2>
          <button
            className="rank-check-modal-popup-close-btn"
            onClick={onClose}
          ></button>
        </div>

        {/* 광고 정보 */}
        <div className="rank-check-modal-popup-ad-info">
          <div className="rank-check-modal-popup-ad-name-cell">
            <img
              src={adData?.imageUrl || "/logo192.png"}
              alt={adData?.missionName || "광고 이미지"}
              className="rank-check-modal-popup-ad-image"
              onError={(e) => {
                e.target.src = "/logo192.png";
              }}
            />
            <div className="rank-check-modal-popup-ad-name-content">
              <span
                className="rank-check-modal-popup-ad-description"
                title={adData?.storeName || "-"}
              >
                {adData?.storeName || "-"}
              </span>
              <span
                className="rank-check-modal-popup-ad-name-link"
                title={adData?.missionName || "-"}
              >
                {adData?.missionName || "-"}
              </span>
            </div>
          </div>
          <div className="rank-check-modal-popup-keyword-info">
            <span className="rank-check-modal-popup-keyword-label">
              메인키워드:
            </span>
            <span className="rank-check-modal-popup-keyword-name">
              {selectedAdName || "키워드명"}
            </span>
          </div>
        </div>

        {/* 차트 컨테이너 */}
        <div className="rank-check-modal-popup-chart-container">
          {loading ? (
            <div className="rank-check-modal-popup-loading-message">
              로딩 중...
            </div>
          ) : error ? (
            <div className="rank-check-modal-popup-error-message">{error}</div>
          ) : chartData ? (
            <Line data={chartData} options={options} />
          ) : (
            <div className="rank-check-modal-popup-no-data-message">
              차트 데이터가 없습니다.
            </div>
          )}
        </div>

        {/* 오버레이 메시지 */}
        {overlayMessage && (
          <div className="rank-check-modal-popup-overlay-message">
            <div className="rank-check-modal-popup-overlay-content">
              {overlayMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RankCheckModal;
